import { StyleSheet, css } from 'aphrodite'
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import LandingSection from '../components/LandingSection'
import StakeholderCardSection from '../components/StakeholderCardSection'
import LandingFooter from '../components/navigation/LandingFooter'
import LandingHeader from '../components/navigation/LandingHeader'
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel'

export default function LandingPage() {
  const [activeCard, setActiveCard] = useState(1)
  const [api, setApi] = useState<CarouselApi>()
  const navigate = useNavigate()

  const handleLearnMoreClick = () => {
    window.scrollTo(0, 0)
    navigate('/digital-ecosystem')
  }

  const handleJoinCommunityClick = () => {
    window.scrollTo(0, 0)
    navigate('/register')
  }

  const handleHeroLearnMoreClick = () => {
    window.scrollTo(0, 0)
    navigate('/community')
  }

  useEffect(() => {
    if (!api) {
      return
    }

    const onSelect = () => {
      setActiveCard(api.selectedScrollSnap() + 1)
    }

    api.on('select', onSelect)
    onSelect()

    return () => {
      api?.off('select', onSelect)
    }
  }, [api])
  return (
    <div className={css(styles.container)}>
      <LandingHeader />
      {/* Hero Section */}
      <section className={css(styles.heroSection)}>
        <div className={css(styles.heroContainer)}>
          <div className={css(styles.heroGrid)}>
            <div className={css(styles.heroContent)}>
              <h1 className={css(styles.heroTitle)}>
                A TRUSTED <br />
                DUMP TRUCK COMMUNITY
              </h1>
              <p className={css(styles.heroDescription)}>
                Transparency, Trust, and Collaboration are the pillars of the
                Community.
              </p>
              <div className={css(styles.heroButtons)}>
                <button
                  className={css(styles.landingPrimaryButton)}
                  onClick={handleJoinCommunityClick}
                >
                  Join the Ecosystem
                </button>
                <button
                  className={css(styles.landingSecondaryButton)}
                  onClick={handleHeroLearnMoreClick}
                >
                  Learn More
                </button>
              </div>
            </div>
            <div className={css(styles.heroImageContainer)}>
              <img
                src="/hero4.webp"
                alt="Dump Truck"
                className={css(styles.heroImage)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className={css(styles.divider)} />

      {/* Features Section */}
      <section className={css(styles.section, styles.sectionWhite)}>
        <div className={css(styles.featuresRow)}>
          <div className={css(styles.featuresTitleCol)}>
            <h2 className={css(styles.title)}>
              Welcome to the <br /> future of the Dump <br />
              Truck Industry
            </h2>
          </div>
          <div className={css(styles.featuresGridCol)}>
            <div className={css(styles.grid3)}>
              <div className={css(styles.featureCard)}>
                <div
                  className={css(styles.featureIcon, styles.featureIconBlue)}
                >
                  <img
                    src="/purpose_icon.png"
                    alt="Purpose Icon"
                    style={{ width: 64, height: 64 }}
                  />
                </div>
                <h3 className={css(styles.featureTitle)}>Purpose</h3>
                <p className={css(styles.featureDescription)}>
                  Net zero emissions and prosperity unite the Community
                </p>
              </div>
              <div className={css(styles.featureCard)}>
                <div
                  className={css(styles.featureIcon, styles.featureIconGreen)}
                >
                  <img
                    src="/vision_icon.png"
                    alt="Vision Icon"
                    style={{ width: 64, height: 64 }}
                  />
                </div>
                <h3 className={css(styles.featureTitle)}>Vision</h3>
                <p className={css(styles.featureDescription)}>
                  A Community striving for Transparency, Trust, and carbon
                  neutrality
                </p>
              </div>
              <div className={css(styles.featureCard)}>
                <div
                  className={css(styles.featureIcon, styles.featureIconPurple)}
                >
                  <img
                    src="/mission_icon.png"
                    alt="Mission Icon"
                    style={{ width: 64, height: 64 }}
                  />
                </div>
                <h3 className={css(styles.featureTitle)}>Mission</h3>
                <p className={css(styles.featureDescription)}>
                  Together, the Community leverages powerful data and innovative
                  solutions
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className={css(styles.whoWeAreSection)}>
        <div className="w-full">
          <div className={css(styles.whoWeAreCard)}>
            <h2 className={css(styles.whoWeAreTitle)}>WHO WE ARE</h2>
            <p className={css(styles.whoWeAreText)}>
              The Ecosystem is a novel, multi-layered technology with an
              innovative architecture for the Dump Truck Industry. Contractors
              (i.e., municipalities, counties, state government, private
              entities), Brokers (i.e., agents, middlemen), and Sub Hauler Corp.
              (i.e., Drivers) collaboratively use the Ecosystem.
            </p>
            <p className={css(styles.whoWeAreText)}>
              Since our founding, we've focused on building trust through
              transparency, providing reliable service, and continuously
              improving the Ecosystem based on feedback from our Community.
            </p>
          </div>
        </div>
      </section>

      {/* Digital Ecosystem Section */}
      <section className={css(styles.digitalEcosystemSection)}>
        <div className={css(styles.container)}>
          <div className={css(styles.digitalEcosystemTopGrid)}>
            <div className={css(styles.digitalEcosystemTopContent)}>
              <h2 className={css(styles.digitalEcosystemTitle)}>
                THE DIGITAL ECOSYSTEM: <br />
                HOW IT WORKS
              </h2>
              <p className={css(styles.digitalEcosystemDescription)}>
                The outdated pen-and-paper model is replaced with a digital
                ecosystem. At the core are individual dashboards, four digital
                templates, and real-time status and progress updates via push
                notifications, all electronically linked under a unique project
                ID -- ensuring full visibility and data integrity.
              </p>
              <button
                className={css(styles.learnMoreButton)}
                onClick={handleLearnMoreClick}
              >
                Learn more
              </button>
            </div>
            <div className={css(styles.digitalEcosystemImageContainer)}>
              <img
                src="/phone.png"
                alt="Mobile App Interface"
                className={css(styles.digitalEcosystemImage)}
                loading="lazy"
              />
            </div>
          </div>
          <div className={css(styles.digitalEcosystemCards)}>
            <div className={css(styles.digitalEcosystemCard)}>
              <img
                src="/digital_tags.png"
                alt="Digital Tags"
                className={css(styles.digitalEcosystemCardImage)}
                loading="lazy"
              />
              <h4 className={css(styles.digitalEcosystemCardTitle)}>
                Digital Tags
              </h4>
              <p className={css(styles.digitalEcosystemCardDescription)}>
                Standardized digital documents that replace paper forms,
                providing transparency and accountability.
              </p>
            </div>
            <div className={css(styles.digitalEcosystemCard)}>
              <img
                src="/digital_terms.png"
                alt="Digital Project Terms"
                className={css(styles.digitalEcosystemCardImage)}
                loading="lazy"
              />
              <h4 className={css(styles.digitalEcosystemCardTitle)}>
                Digital Project Terms
              </h4>
              <p className={css(styles.digitalEcosystemCardDescription)}>
                Electronically linked records that ensure trustworthiness and
                information transparency.
              </p>
            </div>
            <div className={css(styles.digitalEcosystemCard)}>
              <img
                src="/digital_work.png"
                alt="Digital Dispatches"
                className={css(styles.digitalEcosystemCardImage)}
                loading="lazy"
              />
              <h4 className={css(styles.digitalEcosystemCardTitle)}>
                Digital Dispatches
              </h4>
              <p className={css(styles.digitalEcosystemCardDescription)}>
                Digital dispatches that capture all data, activities, and
                decision-making from start to finish.
              </p>
            </div>
            <div className={css(styles.digitalEcosystemCard)}>
              <img
                src="/digital_work.png"
                alt="Digital Dispatches"
                className={css(styles.digitalEcosystemCardImage)}
                loading="lazy"
              />
              <h4 className={css(styles.digitalEcosystemCardTitle)}>
                Digital Status Reports
              </h4>
              <p className={css(styles.digitalEcosystemCardDescription)}>
                A semi-automated status report regarding the progress of the
                hauling project.
              </p>
            </div>
            <div className={css(styles.digitalEcosystemCard)}>
              <img
                src="/digital_work.png"
                alt="Digital Dispatches"
                className={css(styles.digitalEcosystemCardImage)}
                loading="lazy"
              />
              <h4 className={css(styles.digitalEcosystemCardTitle)}>
                Digital Invoices
              </h4>
              <p className={css(styles.digitalEcosystemCardDescription)}>
                2 standardized Invoices are made available, one from the Sub
                Hauler to the Broker and the other from the Broker to the
                Contractor.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tailored Solutions Section */}
      <StakeholderCardSection
        title="TAILORED SOLUTIONS FOR EVERY ROLE"
        backgroundColor="#f9fafb"
      />

      {/* Smart Tag Management Section */}
      <section className={css(styles.smartTagSection)}>
        <div className={css(styles.smartTagGrid)}>
          <div className={css(styles.smartTagContent)}>
            <h2 className={css(styles.smartTagTitle)}>
              WORK SMARTER.
              <br />
              DRIVE CLEANER.
              <br />
              STAY AHEAD.
            </h2>
            <button className={css(styles.smartTagButton)}>
              Join the Ecosystem
            </button>
            <div className="flex gap-2 md:gap-3 lg:gap-4 justify-center lg:justify-start">
              <button
                className={`w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center border-none cursor-pointer font-bold text-base md:text-lg lg:text-xl transition-all duration-200 hover:scale-105 ${
                  activeCard === 1
                    ? 'bg-[#102E65] text-white shadow-lg'
                    : 'text-[#102E65] bg-[#D3EDFF] hover:bg-[#C1E4FF]'
                }`}
                onClick={() => {
                  setActiveCard(1)
                  api?.scrollTo(0)
                }}
              >
                1
              </button>
              <button
                className={`w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center border-none cursor-pointer font-bold text-base md:text-lg lg:text-xl transition-all duration-200 hover:scale-105 ${
                  activeCard === 2
                    ? 'bg-[#102E65] text-white shadow-lg'
                    : 'text-[#102E65] bg-[#D3EDFF] hover:bg-[#C1E4FF]'
                }`}
                onClick={() => {
                  setActiveCard(2)
                  api?.scrollTo(1)
                }}
              >
                2
              </button>
              <button
                className={`w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center border-none cursor-pointer font-bold text-base md:text-lg lg:text-xl transition-all duration-200 hover:scale-105 ${
                  activeCard === 3
                    ? 'bg-[#102E65] text-white shadow-lg'
                    : 'text-[#102E65] bg-[#D3EDFF] hover:bg-[#C1E4FF]'
                }`}
                onClick={() => {
                  setActiveCard(3)
                  api?.scrollTo(2)
                }}
              >
                3
              </button>
            </div>
          </div>
          <div className={css(styles.smartTagImageContainer)}>
            <Carousel
              className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg"
              setApi={setApi}
            >
              <CarouselContent>
                <CarouselItem>
                  <div className={css(styles.smartTagCard)}>
                    <div className={css(styles.smartTagCardImageContainer)}>
                      <img
                        src="/smart_tag_1_1.png"
                        alt="Smart Tag Management"
                        className={css(styles.smartTagCardImage)}
                        loading="lazy"
                      />
                    </div>
                    <div className={css(styles.smartTagCardContent)}>
                      <h4 className={css(styles.smartTagCardTitle)}>
                        Smart Tag Management
                      </h4>
                      <p className={css(styles.smartTagCardDescription)}>
                        Tags, real-time Status and Progress Reports, and
                        semi-automated Invoices are exchanged electronically.
                      </p>
                    </div>
                  </div>
                </CarouselItem>
                <CarouselItem>
                  <div className={css(styles.smartTagCard)}>
                    <div className={css(styles.smartTagCardImageContainer)}>
                      <img
                        src="/smart_tag_2.png"
                        alt="Ditch Paperwork"
                        className={css(styles.smartTagCardImage)}
                        loading="lazy"
                      />
                    </div>
                    <div className={css(styles.smartTagCardContent)}>
                      <h4 className={css(styles.smartTagCardTitle)}>
                        Ditch the Paperwork, Keep the Progress
                      </h4>
                      <p className={css(styles.smartTagCardDescription)}>
                        Stop chasing paper. Start working smarter. The Digital
                        Ecosystem removes the chaos of printed forms, lost Tags,
                        and manually reporting the status of the hauling
                        project.
                      </p>
                    </div>
                  </div>
                </CarouselItem>
                <CarouselItem>
                  <div className={css(styles.smartTagCard)}>
                    <div className={css(styles.smartTagCardImageContainer)}>
                      <img
                        src="/contractors.webp"
                        alt="Digital Dispatches"
                        className={css(styles.smartTagCardImage)}
                        loading="lazy"
                      />
                    </div>
                    <div className={css(styles.smartTagCardContent)}>
                      <h4 className={css(styles.smartTagCardTitle)}>
                        Greener Loads, Cleaner Roads
                      </h4>
                      <p className={css(styles.smartTagCardDescription)}>
                        Carbon is being cut—not corners. By eliminating paper
                        and unnecessary driving, emissions are reduced while
                        saving time and fuel.
                      </p>
                    </div>
                  </div>
                </CarouselItem>
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Join Digital Ecosystem Section */}
      <LandingSection
        title="JOIN THE ECOSYSTEM"
        description="Work smarter with real-time updates, instant data access, and modular, digital templates and workflows. Cut down on paperwork, build trust, and reduce unnecessary driving – all in one Ecosystem."
        imageSrc="/ecosystem2.webp"
        imageAlt="Construction Team"
        buttonText="Join the Ecosystem"
        testimonialText="Trusted by 10,000+ operators,"
        userCount="contractors and haulers"
        onButtonClick={handleJoinCommunityClick}
      />
      <LandingFooter />
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#ffffff',
  },
  divider: {
    height: '1px',
    backgroundColor: '#e5e7eb', // Light gray color
    width: '100%',
    margin: '0',
  },
  header: {
    backgroundColor: '#ffffff',
    boxShadow:
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  },
  headerContainer: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 16px',
    '@media (min-width: 640px)': {
      padding: '0 24px',
    },
    '@media (min-width: 1024px)': {
      padding: '0 32px',
    },
  },

  heroSection: {
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    padding: '4rem 1rem 4rem 1rem',
    minHeight: 'calc(100vh - 64px)',
    '@media (min-width: 768px)': {
      paddingLeft: '2rem',
    },
    '@media (min-width: 1024px)': {
      height: 'calc(100vh - 64px)',
      paddingLeft: '3rem',
      paddingTop: '0',
      paddingBottom: '0',
      paddingRight: '0',
    },
    '@media (min-width: 1280px)': {
      paddingLeft: '5rem',
    },
  },
  heroContainer: {
    margin: '0 auto',
    width: '100%',
  },
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '0rem',
    alignItems: 'center',
    width: '100%',
    '@media (min-width: 1024px)': {
      gridTemplateColumns: '1fr 2fr',
    },
  },
  heroImageContainer: {
    height: '100%',
    width: '100%',
    position: 'relative',
    display: 'flex',
  },
  heroContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
    textAlign: 'center',
    '@media (min-width: 1024px)': {
      textAlign: 'left',
      gap: '32px',
    },
  },
  heroTitle: {
    fontSize: '36px',
    fontWeight: 400,
    color: '#102E65',
    lineHeight: '1.2',
    fontFamily: '"Anton", sans-serif',
    letterSpacing: '1px',
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  heroDescription: {
    fontSize: '18px',
    color: '#4b5563',
    lineHeight: '1.6',
  },
  heroButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'center',
    marginBottom: '2rem',
    '@media (min-width: 640px)': {
      flexDirection: 'row',
      justifyContent: 'center',
    },
    '@media (min-width: 1024px)': {
      justifyContent: 'flex-start',
    },
    fontWeight: 600,
  },
  landingPrimaryButton: {
    backgroundColor: '#6DC3FF',
    color: '#102E65',
    padding: '10px 32px',
    borderRadius: '24px',
    fontSize: '16px',
    textAlign: 'center',
  },
  landingSecondaryButton: {
    border: '1px solid #1F407E',
    color: '#1F407E',
    backgroundColor: 'transparent',
    padding: '10px 32px',
    borderRadius: '24px',
    fontSize: '16px',
    textAlign: 'center',
  },
  heroImage: {
    width: '100%',
    height: 'auto',
    maxHeight: 'calc(100vh - 128px)',
    objectFit: 'contain',
    borderRadius: '16px 0 0 16px',
    '@media (min-width: 1600px)': {
      objectFit: 'cover',
      objectPosition: 'left center',
    },
  },
  section: {
    padding: '40px 0',
    '@media (min-width: 768px)': {
      padding: '60px 0',
    },
    '@media (min-width: 1024px)': {
      padding: '80px 0',
    },
  },
  sectionWhite: {
    backgroundColor: '#ffffff',
  },
  sectionGray: {
    backgroundColor: '#f9fafb',
  },
  sectionBlue: {
    background: 'linear-gradient(to right, #1e3a8a, #1e40af)',
    color: '#ffffff',
  },
  sectionDark: {
    backgroundColor: '#0f172a',
    color: '#ffffff',
  },
  featuresRow: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 1rem',
    gap: '2rem',
    '@media (min-width: 900px)': {
      flexDirection: 'row',
      alignItems: 'flex-start',
      padding: '0 2rem',
    },
    color: '#102E65',
  },
  featuresTitleCol: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    '@media (max-width: 899px)': {
      marginBottom: '32px',
      justifyContent: 'center',
      textAlign: 'center',
    },
  },
  featuresGridCol: {
    flex: 1,
  },
  grid3: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '32px',
    '@media (min-width: 768px)': {
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '24px',
    },
    '@media (min-width: 1024px)': {
      gap: '32px',
    },
  },
  featureCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      textAlign: 'left',
    },
  },
  featureIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    display: 'flex',
    alignSelf: 'center',
    '@media (min-width: 768px)': {
      alignSelf: 'flex-start',
    },
  },
  featureIconBlue: {
    backgroundColor: '#dbeafe',
  },
  featureIconGreen: {
    backgroundColor: '#d1fae5',
  },
  featureIconPurple: {
    backgroundColor: '#e9d5ff',
  },
  featureTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#102E65',
    marginBottom: '8px',
    '@media (min-width: 768px)': {
      fontSize: '20px',
    },
  },
  featureDescription: {
    color: '#4b5563',
    fontSize: '14px',
    lineHeight: '1.5',
    '@media (min-width: 768px)': {
      fontSize: '16px',
    },
  },
  aboutContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  aboutTitle: {
    fontSize: '48px',
    fontWeight: 'bold',
  },
  aboutText: {
    fontSize: '20px',
    lineHeight: '1.6',
  },
  aboutTextLarge: {
    fontSize: '18px',
    lineHeight: '1.6',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '24px',
    marginTop: '32px',
  },
  statNumber: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#93c5fd',
  },
  statLabel: {
    color: '#bfdbfe',
  },
  ecosystemContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  ecosystemTitle: {
    fontSize: '48px',
    fontWeight: 'bold',
    color: '#111827',
  },
  ecosystemDescription: {
    fontSize: '20px',
    color: '#4b5563',
    lineHeight: '1.6',
  },
  stepsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  stepItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  stepNumber: {
    width: '24px',
    height: '24px',
    backgroundColor: '#2563eb',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '4px',
  },
  stepNumberText: {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontWeight: 600,
    color: '#111827',
  },
  stepDescription: {
    color: '#4b5563',
  },
  mobileImage: {
    maxWidth: '384px',
    width: '100%',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    borderRadius: '24px',
    margin: '0 auto',
    display: 'block',
  },
  digitalFeaturesGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '32px',
    '@media (min-width: 768px)': {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
  },
  digitalFeatureCard: {
    padding: '32px',
    borderRadius: '8px',
    textAlign: 'center',
  },
  digitalFeatureCardBlue: {
    backgroundColor: '#eff6ff',
  },
  digitalFeatureCardGreen: {
    backgroundColor: '#f0fdf4',
  },
  digitalFeatureCardPurple: {
    backgroundColor: '#faf5ff',
  },
  digitalFeatureIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  digitalFeatureIconBlue: {
    backgroundColor: '#2563eb',
  },
  digitalFeatureIconGreen: {
    backgroundColor: '#059669',
  },
  digitalFeatureIconPurple: {
    backgroundColor: '#7c3aed',
  },
  solutionsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '32px',
    '@media (min-width: 768px)': {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
  },
  solutionCard: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
  },
  solutionImage: {
    width: '100%',
    height: '192px',
    objectFit: 'cover',
  },
  solutionContent: {
    padding: '24px',
  },
  solutionTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '16px',
  },
  solutionDescription: {
    color: '#4b5563',
    marginBottom: '24px',
  },
  solutionFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '24px',
  },
  solutionFeature: {
    display: 'flex',
    alignItems: 'center',
    color: '#374151',
  },
  solutionButton: {
    width: '100%',
  },
  solutionButtonRed: {
    backgroundColor: '#dc2626',
    ':hover': {
      backgroundColor: '#b91c1c',
    },
  },
  solutionButtonYellow: {
    backgroundColor: '#d97706',
    ':hover': {
      backgroundColor: '#b45309',
    },
  },
  solutionButtonGreen: {
    backgroundColor: '#059669',
    ':hover': {
      backgroundColor: '#047857',
    },
  },
  smartTagSection: {
    color: '#ffffff',
    minHeight: '100vh',
    padding: '32px 0.75rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '@media (min-width: 480px)': {
      padding: '40px 1rem',
    },
    '@media (min-width: 768px)': {
      padding: '60px 2rem',
      minHeight: '60vh',
    },
    '@media (min-width: 1024px)': {
      padding: '80px 3rem',
    },
    '@media (min-width: 1280px)': {
      padding: '100px 5rem',
    },
  },
  smartTagGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    alignItems: 'center',
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    '@media (min-width: 480px)': {
      gap: '28px',
    },
    '@media (min-width: 768px)': {
      gap: '32px',
    },
    '@media (min-width: 1024px)': {
      flexDirection: 'row',
      gap: '48px',
      alignItems: 'stretch',
    },
  },
  smartTagContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '0 0.5rem',
    textAlign: 'center',
    width: '100%',
    '@media (min-width: 480px)': {
      gap: '24px',
      padding: '0 1rem',
    },
    '@media (min-width: 768px)': {
      gap: '28px',
      padding: '0',
    },
    '@media (min-width: 1024px)': {
      flex: '1',
      textAlign: 'left',
      gap: '32px',
    },
  },
  smartTagTitle: {
    fontSize: '24px',
    fontWeight: 400,
    lineHeight: '1.1',
    fontFamily: '"Anton", sans-serif',
    color: '#102E65',
    textAlign: 'center',
    margin: 0,
    '@media (min-width: 480px)': {
      fontSize: '28px',
    },
    '@media (min-width: 768px)': {
      fontSize: '40px',
      lineHeight: '1.2',
    },
    '@media (min-width: 1024px)': {
      fontSize: '48px',
      textAlign: 'left',
    },
    '@media (min-width: 1280px)': {
      fontSize: '60px',
    },
  },
  smartTagButton: {
    backgroundColor: '#6DC3FF',
    color: '#102E65',
    padding: '12px 24px',
    borderRadius: '9999px',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '1.5',
    border: 'none',
    cursor: 'pointer',
    alignSelf: 'center',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#5ab3ef',
      transform: 'translateY(-1px)',
    },
    '@media (min-width: 480px)': {
      padding: '14px 28px',
      fontSize: '15px',
    },
    '@media (min-width: 768px)': {
      padding: '16px 32px',
      fontSize: '16px',
    },
    '@media (min-width: 1024px)': {
      alignSelf: 'flex-start',
    },
  },

  smartTagImageContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    padding: '0 0.5rem',
    '@media (min-width: 480px)': {
      padding: '0 1rem',
    },
    '@media (min-width: 768px)': {
      padding: '0',
    },
    '@media (min-width: 1024px)': {
      flex: '1',
      height: '100%',
    },
  },
  smartTagImageWrapper: {
    position: 'relative',
  },
  smartTagCard: {
    backgroundColor: '#E6EAED',
    borderRadius: '12px',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '320px',
    height: '400px',
    display: 'flex',
    flexDirection: 'column',
    margin: '0 auto',
    '@media (min-width: 480px)': {
      maxWidth: '360px',
      height: '420px',
      borderRadius: '16px',
    },
    '@media (min-width: 768px)': {
      maxWidth: '400px',
      height: '450px',
    },
    '@media (min-width: 1024px)': {
      maxWidth: '100%',
      height: '500px',
    },
  },
  smartTagCardImageContainer: {
    flex: '0 0 70%',
    width: '100%',
    overflow: 'hidden',
    display: 'block',
  },
  smartTagCardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center center',
  },
  smartTagCardContent: {
    padding: '0px 16px 16px 16px',
    flex: '0 0 30%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '@media (min-width: 768px)': {
      padding: '0px 20px 20px 20px',
    },
    '@media (min-width: 1024px)': {
      padding: '0px 24px 24px 24px',
    },
  },
  smartTagCardTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#102E65',
    marginBottom: '8px',
    '@media (min-width: 768px)': {
      fontSize: '18px',
      marginBottom: '10px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '20px',
      marginBottom: '12px',
    },
  },
  smartTagCardDescription: {
    fontSize: '12px',
    color: '#4b5563',
    lineHeight: '1.4',
    margin: 0,
    '@media (min-width: 768px)': {
      fontSize: '13px',
      lineHeight: '1.45',
    },
    '@media (min-width: 1024px)': {
      fontSize: '14px',
      lineHeight: '1.5',
    },
  },
  joinContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  joinTitle: {
    fontSize: '48px',
    fontWeight: 'bold',
  },
  joinDescription: {
    fontSize: '20px',
    lineHeight: '1.6',
  },
  joinFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  joinFeature: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  joinFeatureText: {
    fontSize: '18px',
  },
  joinButton: {
    backgroundColor: '#ffffff',
    color: '#1e3a8a',
    padding: '12px 32px',
    borderRadius: '9999px',
    fontSize: '18px',
    fontWeight: 600,
    textDecoration: 'none',
    display: 'inline-block',
    ':hover': {
      backgroundColor: '#f3f4f6',
    },
  },
  joinTestimonials: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '32px',
  },
  joinAvatars: {
    display: 'flex',
    marginRight: '-8px',
  },
  joinAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: '2px solid #ffffff',
    marginRight: '-8px',
  },
  joinTestimonialText: {
    fontSize: '14px',
    color: '#bfdbfe',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#102E65',
    marginBottom: '16px',
    lineHeight: '1.2',
    paddingLeft: '0',
    '@media (min-width: 768px)': {
      fontSize: '28px',
    },
    '@media (min-width: 900px)': {
      fontSize: '32px',
    },
  },
  whoWeAreSection: {
    minHeight: '90vh',
    minWidth: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundImage: 'url("/who_we_are2.webp")',
    backgroundSize: 'cover',
    backgroundPosition: 'center bottom',
    backgroundRepeat: 'no-repeat',
    padding: '60px 0',
    position: 'relative',
  },
  whoWeAreCard: {
    background: '#fff',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    padding: '60px',
    maxWidth: '500px',
    zIndex: 2,
    marginLeft: '1rem',
    '@media (min-width: 1024px)': {
      marginLeft: '3rem',
    },
    '@media (min-width: 1280px)': {
      marginLeft: '5rem',
    },
    '@media (max-width: 900px)': {
      margin: '0 auto',
      padding: '24px',
    },
  },
  whoWeAreTitle: {
    fontSize: '60px',
    fontWeight: 400,
    marginBottom: '24px',
    fontFamily: '"Anton", sans-serif',
    color: '#102E65',
  },
  whoWeAreText: {
    fontSize: '16px',
    marginBottom: '16px',
    color: '#4B5563',
    fontWeight: 400,
  },
  whoWeAreStats: {
    display: 'flex',
    gap: '32px',
    marginTop: '32px',
  },
  whoWeAreStatNumber: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#2563eb',
  },
  whoWeAreStatLabel: {
    color: '#6b7280',
    fontSize: '16px',
  },
  digitalEcosystemSection: {
    padding: '40px 1rem',
    '@media (min-width: 768px)': {
      padding: '60px 2rem',
    },
    '@media (min-width: 1024px)': {
      padding: '80px 3rem',
    },
    '@media (min-width: 1280px)': {
      padding: '100px 5rem',
    },
  },
  digitalEcosystemTopGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '32px',
    alignItems: 'center',
    marginBottom: '32px',
    '@media (min-width: 1024px)': {
      gridTemplateColumns: '1fr 1fr',
      gap: '48px',
      marginBottom: '48px',
    },
  },
  digitalEcosystemTopContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    textAlign: 'center',
    '@media (min-width: 1024px)': {
      gap: '24px',
      textAlign: 'left',
    },
  },
  digitalEcosystemTitle: {
    fontSize: '32px',
    fontWeight: 400,
    color: '#102E65',
    lineHeight: '1.2',
    fontFamily: '"Anton", sans-serif',
    '@media (min-width: 768px)': {
      fontSize: '48px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  digitalEcosystemDescription: {
    fontSize: '14px',
    color: '#4B5563',
    lineHeight: '1.5',
    fontWeight: 400,
    '@media (min-width: 768px)': {
      fontSize: '16px',
    },
  },
  digitalEcosystemCards: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '16px',
    '@media (min-width: 768px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '20px',
    },
    '@media (min-width: 1024px)': {
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '20px',
    },
    '@media (min-width: 1200px)': {
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: '16px',
    },
  },
  digitalEcosystemCard: {
    backgroundColor: '#D3EDFF',
    padding: '20px',
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    textAlign: 'center',
    minHeight: '50vw',
    '@media (min-width: 768px)': {
      padding: '24px',
      borderRadius: '20px',
      gap: '14px',
      minHeight: '25vw',
    },
    '@media (min-width: 1024px)': {
      padding: '24px',
      borderRadius: '20px',
      gap: '14px',
      textAlign: 'left',
      minHeight: '20vw',
    },
    '@media (min-width: 1200px)': {
      padding: '16px',
      borderRadius: '16px',
      gap: '12px',
      textAlign: 'left',
      minHeight: '12vw',
    },
  },
  digitalEcosystemCardIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    color: '#ffffff',
  },
  digitalEcosystemIconBlue: {
    backgroundColor: '#2563eb',
  },
  digitalEcosystemIconGreen: {
    backgroundColor: '#059669',
  },
  digitalEcosystemIconPurple: {
    backgroundColor: '#7c3aed',
  },
  digitalEcosystemCardTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#102E65',
    margin: 0,
    '@media (min-width: 768px)': {
      fontSize: '18px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '20px',
    },
  },
  digitalEcosystemCardDescription: {
    fontSize: '12px',
    color: '#6b7280',
    lineHeight: '1.5',
    margin: 0,
    '@media (min-width: 768px)': {
      fontSize: '13px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '14px',
    },
  },
  digitalEcosystemCardImage: {
    width: '48px',
    height: '48px',
    objectFit: 'contain',
    alignSelf: 'center',
    '@media (min-width: 768px)': {
      width: '56px',
      height: '56px',
    },
    '@media (min-width: 1024px)': {
      width: '64px',
      height: '64px',
      alignSelf: 'flex-start',
    },
    '@media (min-width: 1200px)': {
      width: '64px',
      height: '64px',
      alignSelf: 'flex-start',
    },
  },
  digitalEcosystemImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    paddingRight: '0',
    '@media (min-width: 1024px)': {
      justifyContent: 'flex-end',
      paddingRight: '80px',
    },
  },
  digitalEcosystemImage: {
    maxWidth: '250px',
    width: '100%',
    backgroundColor: 'transparent',
    '@media (min-width: 768px)': {
      maxWidth: '280px',
    },
    '@media (min-width: 1024px)': {
      maxWidth: '300px',
    },
  },
  learnMoreButton: {
    backgroundColor: '#6DC3FF',
    color: '#102E65',
    padding: '10px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    textAlign: 'center',
    width: '140px',
    height: '40px',
    fontWeight: 600,
    alignSelf: 'center',
    '@media (min-width: 768px)': {
      padding: '11px 18px',
      borderRadius: '22px',
      fontSize: '15px',
      width: '160px',
      height: '44px',
    },
    '@media (min-width: 1024px)': {
      padding: '12px 20px',
      borderRadius: '24px',
      fontSize: '16px',
      width: '186px',
      height: '48px',
      alignSelf: 'flex-start',
    },
  },
})
